/**
 * Lazy Highcharts loader.
 *
 * Dynamically imports the Highcharts core (and optionally its exporting,
 * offline-exporting, and accessibility modules) so they don't ship in the
 * main bundle. The loaded instance is cached at module scope so subsequent
 * callers reuse the same Highcharts singleton — modules registered against
 * it on the first call are still installed on later calls.
 */

// The `highcharts` package's published types don't declare a default export
// even though `import Highcharts from 'highcharts'` works at value-position
// via esModuleInterop. Reference the namespace `typeof import('highcharts')`
// directly as the Highcharts instance type.
type HighchartsDefault = typeof import('highcharts');

type HighchartsModuleName = 'exporting' | 'offline-exporting' | 'accessibility';

let cached: HighchartsDefault | null = null;
let inflight: Promise<HighchartsDefault> | null = null;
const registeredModules = new Set<HighchartsModuleName>();
// Per-module in-flight import promises. Concurrent callers asking for the
// same module share a single dynamic-import promise so we don't double-fetch
// the same chunk. Entries are kept after success — once a module's chunk is
// resolved the promise resolves cheaply, matching the registration cache's
// process-lifetime semantics. On failure we evict so a later retry can try
// again rather than re-throwing the cached error forever.
const moduleInflight = new Map<HighchartsModuleName, Promise<unknown>>();

async function importHighchartsModule(name: HighchartsModuleName): Promise<unknown> {
  switch (name) {
    case 'exporting':
      return import('highcharts/modules/exporting');
    case 'offline-exporting':
      return import('highcharts/modules/offline-exporting');
    case 'accessibility':
      return import('highcharts/modules/accessibility');
  }
}

/**
 * Dedup wrapper around {@link importHighchartsModule}. Concurrent callers
 * receive the same in-flight promise. Successful resolutions are cached for
 * the process lifetime; failures are evicted so the next caller can retry.
 */
function getModule(name: HighchartsModuleName): Promise<unknown> {
  const existing = moduleInflight.get(name);
  if (existing) return existing;
  const promise = importHighchartsModule(name).catch((err) => {
    moduleInflight.delete(name);
    throw err;
  });
  moduleInflight.set(name, promise);
  return promise;
}

function registerModule(
  Highcharts: HighchartsDefault,
  name: HighchartsModuleName,
  imported: unknown,
): void {
  if (registeredModules.has(name)) return;
  // Highcharts module files ship as either an ESM default export or a CJS
  // module — unwrap whichever shape we got before invoking.
  const fn = (imported as { default?: unknown })?.default ?? imported;
  if (typeof fn === 'function') {
    (fn as (hc: HighchartsDefault) => void)(Highcharts);
    registeredModules.add(name);
  }
}

export async function loadHighcharts(
  withModules?: Array<HighchartsModuleName>,
): Promise<HighchartsDefault> {
  if (cached) {
    if (withModules && withModules.length > 0) {
      for (const name of withModules) {
        if (!registeredModules.has(name)) {
          const imported = await getModule(name);
          registerModule(cached, name, imported);
        }
      }
    }
    return cached;
  }

  if (!inflight) {
    inflight = (async () => {
      const mod = await import('highcharts');
      // Highcharts is published as a CJS module without an explicit default
      // export. Bundlers synthesize one at runtime via esModuleInterop, but
      // some configurations leave the namespace object itself usable as the
      // Highcharts instance. Accept either shape.
      const Highcharts = ((mod as unknown as { default?: HighchartsDefault }).default
        ?? (mod as unknown as HighchartsDefault));
      cached = Highcharts;
      return Highcharts;
    })();
  }

  const Highcharts = await inflight;

  if (withModules && withModules.length > 0) {
    for (const name of withModules) {
      if (!registeredModules.has(name)) {
        const imported = await getModule(name);
        registerModule(Highcharts, name, imported);
      }
    }
  }

  return Highcharts;
}
